import React from 'react';
import { getTenantLogoUrl } from '@/assets/tenant-logos';
import { isLearn41Domain } from '@/auth/useTenant';

interface TenantLogoProps {
  /** Tenant domain (e.g., "think41.com", "demo") */
  domain: string;
  /** Tenant display name (used for fallback avatar and text) */
  name: string;
  /** CSS classes for the logo image */
  className?: string;
  /** CSS classes for the fallback avatar circle */
  avatarClassName?: string;
  /** CSS classes for the tenant name text */
  nameClassName?: string;
  /** Show tenant name alongside logo (default: false) */
  showNameWithLogo?: boolean;
  /** Compact mode: show only primary logo without co-branding (default: false) */
  compact?: boolean;
}

/**
 * TenantLogo Component
 *
 * Displays a tenant's logo if available (bundled via import.meta.glob),
 * otherwise falls back to showing an avatar with the first letter + tenant name.
 *
 * Benefits:
 * - COEP-compliant (all logos are bundled)
 * - No 404 errors (synchronous check)
 * - Consistent fallback behavior
 */
export const TenantLogo: React.FC<TenantLogoProps> = ({
  domain,
  name,
  className = 'h-8 sm:h-9 md:h-10 max-w-full',
  avatarClassName = 'h-10 w-10 rounded-full bg-primary flex items-center justify-center text-white font-bold text-xl',
  nameClassName = 'text-xl font-bold text-text-primary group-hover:text-primary transition-colors',
  showNameWithLogo = false,
  compact = false,
}) => {
  const isLearn41 = isLearn41Domain();
  const learn41LogoUrl = isLearn41 ? getTenantLogoUrl('learn41') : null;
  const tenantLogoUrl = getTenantLogoUrl(domain);

  // If learn41 theme, show learn41 logo + tenant logo side by side
  if (isLearn41 && learn41LogoUrl) {
    return (
      <>
        <img src={learn41LogoUrl} alt="Learn41 Logo" className={`${className} object-contain`} />
        {!compact && tenantLogoUrl && (
          <>
            <span className="mx-2 text-border-primary text-lg font-light">×</span>
            <img src={tenantLogoUrl} alt={`${name} Logo`} className={`${className} object-contain`} />
          </>
        )}
        {!compact && showNameWithLogo && !tenantLogoUrl && <h1 className={nameClassName}>{name}</h1>}
      </>
    );
  }

  const logoUrl = tenantLogoUrl;

  // If logo exists, show it
  if (logoUrl) {
    return (
      <>
        <img src={logoUrl} alt={`${name} Logo`} className={className} />
        {showNameWithLogo && <h1 className={nameClassName}>{name}</h1>}
      </>
    );
  }

  // Fallback: show avatar + name
  const avatarLetter = name.charAt(0).toUpperCase();

  return (
    <>
      <div className={avatarClassName}>{avatarLetter}</div>
      <h1 className={nameClassName}>{name}</h1>
    </>
  );
};
